import React, { useEffect, useState } from "react";
import { Container, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const FaqsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const PAGE_SLUG = "faqs";

    // Dynamic & Editable State Variables
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [faqs, setFaqs] = useState([{ question: "", answer: "" }]);

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ size: ["small", false, "large", "huge"] }],
            ["bold", "italic", "underline", "strike", "blockquote"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "clean"],
        ],
    };

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
    });

    const fetchPageData = async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/pages/${PAGE_SLUG}`,
                { headers: getAuthHeaders() }
            );
            if (res.data?.data) {
                // Fetching existing title or setting a default value
                setTitle(res.data.data.title || "Frequently Asked Questions");
                setContent(res.data.data.content || "");
                setFaqs(
                    res.data.data.faqs?.length 
                        ? res.data.data.faqs 
                        : [{ question: "", answer: "" }]
                );
            }
        } catch (err) {
            if (err.response?.status !== 404) {
                setError(err.response?.data?.message || "Failed to load FAQ page.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPageData();
    }, []);

    const handleFaqChange = (index, field, value) => {
        const updated = [...faqs];
        updated[index][field] = value;
        setFaqs(updated);
        
        if (errors[`faqs.${index}.${field}`]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[`faqs.${index}.${field}`];
                return next;
            });
        }
    };

    const addFaqItem = () => setFaqs([...faqs, { question: "", answer: "" }]);
    const removeFaqItem = (index) => setFaqs(faqs.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");
        setErrors({});

        // Payload with dynamic 'title', 'content', and 'faqs'
        const payload = {
            title,
            content,
            faqs,
        };

        try {
            setSaving(true);
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/${PAGE_SLUG}`,
                payload,
                { headers: getAuthHeaders() }
            );
            setMessage(res.data?.message || "FAQs saved successfully.");
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Please fix validation errors.");
            } else {
                setError(err.response?.data?.message || "Failed to save FAQs.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <h2 className="fw-bold mb-1">FAQ Page Settings</h2>
                <p className="text-muted mb-4">Manage title, rich header text, and dynamic Q&A pairs.</p>

                {message && <Alert variant="success" dismissible onClose={() => setMessage("")}>{message}</Alert>}
                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        
                        {/* Page Information Section */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">Page Information</Card.Header>
                            <Card.Body>
                                {/* Editable Title Input Field */}
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Page Title
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Page Title (e.g. Frequently Asked Questions)"
                                        value={title}
                                        onChange={(e) => {
                                            setTitle(e.target.value);
                                            setErrors((prev) => ({ ...prev, title: "" }));
                                        }}
                                        isInvalid={!!errors.title}
                                        className="bg-light border-0 py-2 px-3"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/* Editable Rich Content Field */}
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Introductory Content
                                    </Form.Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={content}
                                        onChange={(val) => {
                                            setContent(val);
                                            setErrors((prev) => ({ ...prev, content: "" }));
                                        }}
                                        modules={quillModules}
                                        placeholder="Add introductory text or guidelines..."
                                        style={{ minHeight: "220px" }}
                                    />
                                    {errors.content && (
                                        <div className="text-danger small mt-2">
                                            {Array.isArray(errors.content) ? errors.content[0] : errors.content}
                                        </div>
                                    )}
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        {/* FAQs Dynamic List */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0 d-flex justify-content-between align-items-center">
                                <span>FAQ Items</span>
                                <Button size="sm" variant="secondary" onClick={addFaqItem}>
                                    + Add Question
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {errors.faqs && typeof errors.faqs === "string" && (
                                    <Alert variant="danger">{errors.faqs}</Alert>
                                )}

                                {faqs.map((faq, index) => (
                                    <div key={index} className="p-3 bg-light rounded mb-3 position-relative">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-bold small text-muted">Question #{index + 1}</span>
                                            {faqs.length > 1 && (
                                                <Button 
                                                    size="sm" 
                                                    variant="danger" 
                                                    onClick={() => removeFaqItem(index)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter Question"
                                                value={faq.question}
                                                onChange={(e) => handleFaqChange(index, "question", e.target.value)}
                                                isInvalid={!!errors[`faqs.${index}.question`]}
                                                className="bg-white border-0"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors[`faqs.${index}.question`]?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="Enter Answer"
                                                value={faq.answer}
                                                onChange={(e) => handleFaqChange(index, "answer", e.target.value)}
                                                isInvalid={!!errors[`faqs.${index}.answer`]}
                                                className="bg-white border-0"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors[`faqs.${index}.answer`]?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>

                        {/* Submit Button */}
                        <div className="d-flex justify-content-end mb-4">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={saving} 
                                className="px-5 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save FAQs"
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Container>
        </PageLayout>
    );
};

export default FaqsPage;